import React, { useState } from "react"
import {
  testFirebaseStorage,
  testImageUpload,
  checkFirebaseConfig,
} from "./testFirebase"

/**
 * Page de test Firebase Storage
 * À utiliser pendant le développement pour vérifier la configuration
 */
function FirebaseTestPage() {
  const [testResult, setTestResult] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleTest = async () => {
    setLoading(true)
    setTestResult(null)

    const success = await testFirebaseStorage()
    setTestResult(success ? "success" : "error")
    setLoading(false)
  }

  const handleCheckConfig = () => {
    checkFirebaseConfig()
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (file) {
      setLoading(true)
      await testImageUpload(file)
      setLoading(false)
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>🔥 JustNow Firebase Storage</h1>
        <h2 style={styles.subtitle}>
          Test de fonctionnement - Proposition graphique V2
        </h2>
        <p style={styles.subtitle}>
          Utilisez cette page pour vérifier que Firebase Storage est correctement
          configuré
        </p>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Vérifier la configuration</h2>
          <p style={styles.description}>
            Vérifiez que toutes les variables d&aposenvironnement sont présentes
          </p>
          <button onClick={handleCheckConfig} style={styles.button}>
            🔍 Vérifier le .env
          </button>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Test d&apos;upload basique</h2>
          <p style={styles.description}>
            Upload d&apos;un fichier texte pour tester la connexion à Firebase
          </p>
          <button
            onClick={handleTest}
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.primaryButton,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "⏳ Test en cours..." : "🧪 Lancer le test"}
          </button>

          {testResult === "success" && (
            <div style={styles.successBox}>
              ✅ Test réussi ! Firebase Storage fonctionne correctement.
            </div>
          )}

          {testResult === "error" && (
            <div style={styles.errorBox}>
              ❌ Test échoué. Vérifie la console pour plus de détails.
            </div>
          )}
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Test d&apos;upload d&apos;image</h2>
          <p style={styles.description}>
            Upload réel d&apos;une image pour tester le flux complet
          </p>
          <label style={styles.fileLabel}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={styles.fileInput}
            />
            <span style={styles.button}>📸 Choisir une image</span>
          </label>
        </div>

        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>💡 En cas de problème</h3>
          <ul style={styles.list}>
            <li>
              Vérifiez que le fichier <code>.env</code> est à la racine du projet
            </li>
            <li>
              Vérifiez que toutes les variables commencent par{" "}
              <code>REACT_APP_</code>
            </li>
            <li>Redémarrez le serveur après modification du .env</li>
            <li>Vérifiez les règles de sécurité dans Firebase Console</li>
            <li>
              Consultez la console navigateur (F12) pour les erreurs détaillées
            </li>
          </ul>
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Consultez <strong>La documentation JustNow</strong> pour plus d&apos;aide
          </p>
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "40px 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    maxWidth: "800px",
    width: "100%",
    background: "white",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  title: {
    fontSize: "36px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "10px",
    color: "#333",
  },
  subtitle: {
    textAlign: "center",
    color: "#666",
    marginBottom: "40px",
    fontSize: "16px",
  },
  section: {
    marginBottom: "40px",
    padding: "20px",
    background: "#f8f9fa",
    borderRadius: "12px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "10px",
    color: "#333",
  },
  description: {
    color: "#666",
    marginBottom: "20px",
    fontSize: "14px",
  },
  button: {
    display: "inline-block",
    padding: "12px 24px",
    background: "#667eea",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    textAlign: "center",
  },
  primaryButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
  },
  fileLabel: {
    display: "inline-block",
    cursor: "pointer",
  },
  fileInput: {
    display: "none",
  },
  successBox: {
    marginTop: "20px",
    padding: "15px",
    background: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    color: "#155724",
    fontSize: "14px",
  },
  errorBox: {
    marginTop: "20px",
    padding: "15px",
    background: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: "8px",
    color: "#721c24",
    fontSize: "14px",
  },
  infoBox: {
    background: "#e7f3ff",
    padding: "20px",
    borderRadius: "12px",
    border: "2px solid #b3d9ff",
    marginTop: "40px",
  },
  infoTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#004085",
  },
  list: {
    marginLeft: "20px",
    color: "#004085",
    fontSize: "14px",
    lineHeight: "1.8",
  },
  footer: {
    marginTop: "40px",
    padding: "20px",
    textAlign: "center",
    borderTop: "2px solid #e9ecef",
  },
  footerText: {
    color: "#666",
    fontSize: "14px",
  },
}

export default FirebaseTestPage
